import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { ToastrService } from "ngx-toastr";
import { environment } from "@env/environment";
import { SharedService } from "@shared/services/shared.service";
import { AuthService } from "@shared/auth/auth.service";
import { IPartnerAccess } from "app/models/partners/partners";
import { PartnerService } from "app/services/partner-service";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { LabService } from "app/services/lab-service";

@Component({
  selector: "app-partner-administration",
  templateUrl: "./partner-administration.component.html",
  styleUrls: ["./partner-administration.component.scss"],
  providers: [PartnerService],
})
export class PartnerAdministrationComponent implements AfterViewInit {
  partners: IPartnerAccess[] = [];
  loading: boolean = false;
  env = environment;
  resultSize: number = 0;
  displayedColumns: string[] = [
    "id",
    "partnerName",
    "accessStartDate",
    "accessEndDate",
    "accessType",
    "requiresPassKey",
    "requiresDisclaimerApproval",
    "actions",
  ];
  dataSource = new MatTableDataSource<IPartnerAccess>();
  @ViewChild("TablePaginator") paginator: MatPaginator;
  @ViewChild("TableSort", { static: false }) sort: MatSort;

  constructor(
    public dialog: MatDialog,
    public sharedService: SharedService,
    public partnerService: PartnerService,
    private toastr: ToastrService
  ) { }

  ngAfterViewInit(): void {
    this.load();
  }

  filterTable(event: Event) {
    let value = (event.target as HTMLInputElement).value;
    this.dataSource.filter = value.trim().toLowerCase();
  }

  removePartnerAccess(id: number) {
    this.sharedService.openConfirm(
      "Are you sure you want to remove this partner access?"
    );

    this.sharedService.confirmed().subscribe((confirmed) => {
      if (confirmed) {
        this.loading = true;
        this.partnerService.removePartner(id).subscribe(
          (u) => {
            this.load();
            this.toastr.success("Removed partner access");
            this.loading = false;
          },
          (error) => {
            console.error("remove error: " + error.statusText);
            this.loading = false;
            this.toastr.error("Unable to remove partner access");
          }
        );
      }
    });
  }

  load() {
    this.loading = true;
    this.partnerService.getAll().subscribe(
      (u) => {
        this.partners = u;
        this.resultSize = this.partners.length;
        this.dataSource.data = this.partners;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
        this.loading = false;
      },
      (error) => {
        console.error("error: " + error.statusText);
        this.loading = false;
      }
    );
  }

  clipboardCopyPartnerAccess(accessUriGuid: string) {
    this.sharedService.copyToClipboard(
      this.env.baseUrl + "/Partner/Access/" + accessUriGuid
    );
  }

  linkPartnerAccess(accessUriGuid: string) {
    window.open(
      this.env.baseUrl + "/Partner/Access/" + accessUriGuid,
      "_blank"
    );
  }

  openPartnerInfoDialog(partner: IPartnerAccess) {
    const dialogRef = this.dialog.open(DialogPartnerInfoComponent, {
      width: "85%",
      panelClass:
        this.sharedService.userProfile.theme === "dark" ? "theme-dark" : "",
      data: {
        partner: partner,
      },
    });
  }

  openRemovePatnerDialog(partner: IPartnerAccess) {
    const dialogRef = this.dialog.open(DialogRemovePartnerComponent, {
      width: "85%",
      panelClass:
        this.sharedService.userProfile.theme === "dark" ? "theme-dark" : "",
      data: {
        partner: partner,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      this.load();
    });
  }

  openEditPartnerDialog(partner: IPartnerAccess) {
    let p = partner;
    if (partner === null) {
      p = {
        id: 0,
        partnerName: "",
        accessType: "",
        active: true,
        accessUriGuid: "",
        partnerEmail: "",
        partnerPhone: "",
        partnerAddress: "",
        accessStartDate: "",
        accessEndDate: "",
        passKeys: [],
        disclaimer: "",
        requiresDisclaimerApproval: true,
        requiresPassKey: true,
        labId: 0,
        createDate: "",
        lastUpdated: "",
        createdBy: "",
        lastUpdatedBy: "",
      };
    }
    const dialogRef = this.dialog.open(DialogEditPartnerComponent, {
      width: "90%",
      panelClass:
        this.sharedService.userProfile.theme === "dark" ? "theme-dark" : "",
      data: {
        partner: p,
      },
    });
    dialogRef.afterClosed().subscribe((result) => this.dialogClosed(result));
  }

  dialogClosed(result: any): void {
    if (result !== "Cancel") {
      this.load();
    }
  }
}

//Remove partner Dialog
@Component({
  selector: "dialog-remove-partner",
  templateUrl: "remove-partner.html",
})
export class DialogRemovePartnerComponent {
  constructor(@Inject(MAT_DIALOG_DATA) public data: any) { }
}

//Edit Dialog
@Component({
  selector: "dialog-edit-partner",
  templateUrl: "edit-partner.html",
  styleUrls: ["./partner-administration.component.scss"],
  providers: [PartnerService],
})
export class DialogEditPartnerComponent implements OnInit {
  partner: IPartnerAccess = null;
  processing: boolean = false;
  passKeys: any[] = [];
  organizationsControl = new FormControl('');
  filteredOrganizations: Observable<any[]>;
  organizations: any[] = [];

  editForm: FormGroup = null;
  constructor(
    public partnerService: PartnerService,
    private toastr: ToastrService,
    private authService: AuthService,
    public sharedService: SharedService,
    private cd: ChangeDetectorRef,
    private labService: LabService,
    private dialogRef: MatDialogRef<DialogEditPartnerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  ngOnInit(): void {
    if (this.data === null || this.data.partner === null) {
      this.toastr.error("Unable to load partner: null");
      return;
    }

    this.Init();
  }

  private _organizations(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.organizations.filter(option => option.displayName.toLowerCase().includes(filterValue));
  }

  Init() {
    this.processing = true;

    this.labService.getLabs().subscribe(
      (result) => {
        this.organizations = result;
        this.filteredOrganizations = this.organizationsControl.valueChanges.pipe(
          startWith(''),
          map(value => this._organizations(value || '')),
        );

        if (this.data.partner.id > 0) {
          //load partner
          this.processing = true;
          this.partnerService.getPartner(this.data.partner.id).subscribe(
            (u) => {
              this.partner = u;
              this.setFormValues();
              this.processing = false;
            },
            (error) => {
              console.error("error: " + error.statusText);
              this.toastr.error("Unable to load partner: " + this.data.id);
              this.processing = false;
            }
          );
        } else {
          this.partner = this.data.partner;
          this.setFormValues();
          this.processing = false;
        }

        this.cd.detectChanges();
      },
      (error) => {
        this.toastr.error("Unable to load Organizations");
        this.processing = false;
      }
    );
  }

  onOrganizationSelected(id: number) {
    this.partner.labId = id;
  }

  removePassKey(index: number) {
    this.sharedService.openConfirm(
      "Are you sure you want to remove this pass key: " +
      this.passKeys[index].passKey +
      " ?"
    );

    this.sharedService.confirmed().subscribe((confirmed) => {
      if (confirmed) {
        this.passKeys.splice(index, 1);
      }
    });
  }

  addPassKey() {
    //add random 6 digit number
    var passkey = Math.floor(100000 + Math.random() * 900000);
    this.passKeys.push({ passKey: passkey, comments: "" });
    this.cd.detectChanges();
  }

  clickRequiresDisclaimerApproval() { }

  clickRequiresPassKey() {
    this.partner.requiresPassKey = !this.editForm.get("requiresPassKey").value;
    this.cd.detectChanges();
  }

  setFormValues() {
    this.passKeys = this.partner.passKeys;
    if (this.passKeys === null) {
      this.passKeys = [];
    }

    this.editForm = new FormGroup({
      active: new FormControl("true"),
      accessType: new FormControl(""),
      accessUriGuid: new FormControl("", Validators.required),
      partnerName: new FormControl("", Validators.required),
      partnerEmail: new FormControl(""),
      partnerPhone: new FormControl(""),
      partnerAddress: new FormControl(""),
      accessStartDate: new FormControl(new Date()),
      accessEndDate: new FormControl(new Date()),
      disclaimer: new FormControl(this.partnerService.defaultDisclaimer),
      requiresDisclaimerApproval: new FormControl(true),
      requiresPassKey: new FormControl(true),
    });

    if (this.partner.id > 0) {
      this.editForm.get("active").setValue(this.partner.active.toString());
      this.editForm.get("accessType").setValue(this.partner.accessType);
      this.editForm.get("accessUriGuid").setValue(this.partner.accessUriGuid);
      this.editForm.get("partnerName").setValue(this.partner.partnerName);
      this.editForm.get("partnerEmail").setValue(this.partner.partnerEmail);
      this.editForm.get("partnerPhone").setValue(this.partner.partnerPhone);
      this.editForm.get("partnerAddress").setValue(this.partner.partnerAddress);
      this.editForm.get("accessStartDate").setValue(new Date(this.partner.accessStartDate));
      this.editForm.get("accessEndDate").setValue(new Date(this.partner.accessEndDate));
      this.editForm.get("disclaimer").setValue(this.partner.disclaimer);
      this.editForm.get("requiresDisclaimerApproval").setValue(this.partner.requiresDisclaimerApproval);
      this.editForm.get("requiresPassKey").setValue(this.partner.requiresPassKey);
      this.partner.requiresPassKey = this.editForm.get("requiresPassKey").value;
      this.partner.labId = this.partner.labId || 0; // Ensure organizationId is set
      this.organizationsControl.setValue(this.organizations.find(org => org.id === this.partner.labId)?.displayName || '');
    } else {
      this.editForm.get("accessStartDate").setValue("");
      this.editForm.get("accessEndDate").setValue("");
    }
  }

  savePartner() {
    if (
      this.editForm.get("partnerName").value === null ||
      this.editForm.get("partnerName").value.trim() === ""
    ) {
      this.toastr.error("Invalid Partner Name");
      return;
    }
    if (
      this.editForm.get("accessType").value === null ||
      this.editForm.get("accessType").value.trim() === ""
    ) {
      this.toastr.error("Invalid Access Type");
      return;
    }
    if (this.editForm.get("requiresPassKey").value) {
      if (this.passKeys === null || this.passKeys.length === 0) {
        this.toastr.error("Pass Key(s) required");
        return;
      }
    }
    if (this.editForm.get("requiresDisclaimerApproval").value) {
      if (
        this.editForm.get("disclaimer").value === null ||
        this.editForm.get("disclaimer").value.trim() === ""
      ) {
        this.toastr.error("Disclaimer/Data Use Agreement required");
        return;
      }
    }

    this.getFormValues();
    this.processing = true;
    this.partnerService.savePartner(this.partner).subscribe(
      (u) => {
        this.processing = false;
        this.toastr.success("Saved Partner: " + this.partner.partnerName);
        this.dialogRef.close();
      },
      (error) => {
        console.error("error: " + error.statusText);
        this.processing = false;
        this.toastr.error(
          "Unable to save partner: " + this.partner.partnerName
        );
      }
    );
  }

  getFormValues() {
    this.partner.active = this.editForm.get("active").value === "true";
    this.partner.accessType = this.editForm.get("accessType").value;
    this.partner.partnerName = this.editForm.get("partnerName").value;
    this.partner.partnerEmail = this.editForm.get("partnerEmail").value;
    this.partner.partnerPhone = this.editForm.get("partnerPhone").value;
    this.partner.partnerAddress = this.editForm.get("partnerAddress").value;
    this.partner.accessStartDate = this.editForm.get("accessStartDate").value;
    this.partner.accessEndDate = this.editForm.get("accessEndDate").value;
    this.partner.disclaimer = this.editForm.get("disclaimer").value;
    this.partner.requiresDisclaimerApproval = this.editForm.get("requiresDisclaimerApproval").value;
    this.partner.labId = this.organizations.find(org => org.displayName === this.organizationsControl.value)?.id || 0;
    //this.partner.requiresPassKey = this.editForm.get("requiresPassKey").value;
    this.partner.passKeys = this.passKeys;
  }

  clipboardCopyPassKey(item: number) {
    this.sharedService.copyToClipboard(item.toString());
  }
}

//Info Dialog
@Component({
  selector: "dialog-partner-info",
  templateUrl: "partner-info.html",
  providers: [PartnerService],
})
export class DialogPartnerInfoComponent implements OnInit {
  partner: IPartnerAccess = null;
  env = environment;

  constructor(
    public sharedService: SharedService,
    private dialogRef: MatDialogRef<DialogPartnerInfoComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  ngOnInit(): void {
    console.log(this.data);
    this.partner = this.data.partner;
  }

  clipboardCopy() {
    let text = "Partner Name: " + this.partner.partnerName + "\n";
    text += "Partner URL: " + this.env.baseUrl + '/Partner/Access/' + this.partner.accessUriGuid + "\n";
    text += "Partner Email: " + this.partner.partnerEmail + "\n";
    if (this.partner.passKeys.length > 0) {
      text += "Access Keys: \n";
      for (let i = 0; i < this.partner.passKeys.length; i++) {
        text += "Key: " + this.partner.passKeys[i].passKey;
        if (this.partner.passKeys[i].comments !== null && this.partner.passKeys[i].comments !== "")
          text += " | Comments: " + this.partner.passKeys[i].comments;

        text += "\n";
      }
    }
    this.sharedService.copyToClipboard(text);
  }
}
